// ============================================================================
// MyUsagePage.jsx — C3 visão USER (Fatia 6)
// Página /meu-consumo: detalhamento do consumo individual de IA do mês corrente.
// Consome GET /api/me/usage via usageService (cache 60s).
//
// UI_PADRAO compliant:
// - Paleta cinza-base (gray-50/100/200/300/600/700/900)
// - lucide-react outline, strokeWidth padrão
// - Sem cor decorativa; #a93333 apenas para indicar saturação >100%
// - font-medium / font-semibold
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Zap,
  Bot,
  Info,
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSportsLang } from '../contexts/SportsLangContext';
import {
  getMyUsage,
  invalidateUsageCache,
  formatInteractions,
  formatUsageRatio,
} from '../services/usageService';

// ── i18n PT/EN inline ────────────────────────────────────────────────────────
const I18N = {
  pt: {
    backToFeed: 'Voltar ao feed',
    title: 'Meu consumo de IA',
    subtitle: 'Detalhamento das suas interações com assistentes no mês corrente',
    refresh: 'Atualizar',
    refreshing: 'Atualizando...',
    loading: 'Carregando seu consumo...',
    errorTitle: 'Não foi possível carregar seu consumo',
    errorRetry: 'Tentar novamente',
    cardInteractions: 'Interações',
    cardInteractionsHelp: 'Total de chamadas a assistentes de IA no mês',
    cardTokens: 'Tokens',
    cardTokensHelp: 'Soma de tokens (entrada + saída) processados',
    cardCost: 'Cota utilizada',
    cardCostHelp: 'Percentual da cota mensal do clube consumido por você',
    cardCostNoSub: 'Clube sem plano ativo — sem cota definida',
    byAgentTitle: 'Por assistente',
    byAgentEmpty: 'Você ainda não usou nenhum assistente neste mês.',
    colAgent: 'Assistente',
    colInteractions: 'Interações',
    colTokens: 'Tokens',
    contextTitle: 'Contexto do seu plano',
    contextLineClub: (club) => `Plano do clube: ${club}`,
    contextLinePlan: (plan, limit) =>
      `${plan} — ${formatInteractions(limit)} interações/mês para todo o clube`,
    contextHonest:
      'Não existe ainda um limite individual definido. Tudo o que você consome entra no total do clube — peça à pessoa GESTORA do clube para acompanhar a cota total no painel de gestão.',
    contextNoSub:
      'Você ainda não está vinculado a um plano. Fale com a pessoa GESTORA do seu clube ou com o suporte para entender sua cota.',
    asOf: 'Atualizado em',
    monthLabel: 'Período de referência',
  },
  en: {
    backToFeed: 'Back to feed',
    title: 'My AI usage',
    subtitle: 'Breakdown of your assistant interactions this month',
    refresh: 'Refresh',
    refreshing: 'Refreshing...',
    loading: 'Loading your usage...',
    errorTitle: 'Could not load your usage',
    errorRetry: 'Try again',
    cardInteractions: 'Interactions',
    cardInteractionsHelp: 'Total AI assistant calls this month',
    cardTokens: 'Tokens',
    cardTokensHelp: 'Sum of tokens (input + output) processed',
    cardCost: 'Quota used',
    cardCostHelp: 'Percentage of the club monthly quota consumed by you',
    cardCostNoSub: 'Club has no active plan — no quota defined',
    byAgentTitle: 'By assistant',
    byAgentEmpty: 'You have not used any assistant this month.',
    colAgent: 'Assistant',
    colInteractions: 'Interactions',
    colTokens: 'Tokens',
    contextTitle: 'Your plan context',
    contextLineClub: (club) => `Club plan: ${club}`,
    contextLinePlan: (plan, limit) =>
      `${plan} — ${formatInteractions(limit)} interactions/month for the whole club`,
    contextHonest:
      'There is no individual limit defined yet. Everything you use counts toward the club total — ask your club MANAGER to track the overall quota in the management panel.',
    contextNoSub:
      'You are not linked to a plan yet. Talk to your club MANAGER or support to learn about your quota.',
    asOf: 'Updated at',
    monthLabel: 'Reference period',
  },
};

// ── formatadores ─────────────────────────────────────────────────────────────
function formatNumber(n, lang) {
  if (n == null) return '—';
  return n.toLocaleString(lang === 'en' ? 'en-US' : 'pt-BR');
}

function formatPct(interactions, limit) {
  // % da cota (créditos = interações). Sem cor de alerta — UI_PADRAO:
  // vermelho é só clínico; warn de cota é peso de fonte, não cor.
  if (!limit) return null;
  const pct = (Number(interactions || 0) / Number(limit)) * 100;
  return pct < 1 && pct > 0 ? '<1%' : `${Math.round(pct)}%`;
}

function formatAgentName(slug) {
  if (!slug) return '—';
  return slug
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

function formatTimestamp(iso, lang) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === 'en' ? 'en-US' : 'pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Componente principal ─────────────────────────────────────────────────────
const MyUsagePage = () => {
  const { user } = useAuth();
  const { lang } = useSportsLang();
  const t = I18N[lang] || I18N.pt;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = useCallback(
    async ({ force = false } = {}) => {
      if (force) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const res = await getMyUsage({ force });
        setData(res);
        setFetchedAt(new Date().toISOString());
      } catch (err) {
        console.error('[MyUsagePage] erro ao carregar consumo:', err);
        setError(err?.response?.data?.error || err?.message || 'unknown_error');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    if (user?.id) load({ force: false });
  }, [user, load]);

  const handleRefresh = () => {
    invalidateUsageCache();
    load({ force: true });
  };

  // ── Loading state ──
  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-64 bg-gray-200 rounded" />
            <div className="h-4 w-96 bg-gray-200 rounded" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-32 bg-white border border-gray-200 rounded-lg" />
              <div className="h-32 bg-white border border-gray-200 rounded-lg" />
              <div className="h-32 bg-white border border-gray-200 rounded-lg" />
            </div>
            <div className="h-64 bg-white border border-gray-200 rounded-lg" />
          </div>
          <p className="text-sm text-gray-500 mt-6 text-center">{t.loading}</p>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Link
            to="/feed"
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium"
          >
            <ArrowLeft size={16} />
            {t.backToFeed}
          </Link>
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <AlertTriangle
              size={40}
              className="mx-auto mb-4"
              style={{ color: '#a93333' }}
            />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {t.errorTitle}
            </h2>
            <p className="text-sm text-gray-600 mb-6">{String(error)}</p>
            <button
              onClick={() => load({ force: true })}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              <RefreshCw size={14} />
              {t.errorRetry}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { club, subscription, usage_current_month: usage } = data;
  const interactions = usage?.interactions || 0;
  const tokens = usage?.tokens || 0;
  const quotaPct = formatPct(interactions, subscription?.ai_credits_monthly);
  const byAgent = Array.isArray(usage?.by_agent) ? usage.by_agent : [];

  // Texto "X de Y" — usa subscription do CLUBE como ref de cota total
  const usageRatioText = formatUsageRatio({ interactions }, subscription, lang);
  const period = usage?.period_start
    ? `${formatTimestamp(usage.period_start, lang).split(',')[0]}`
    : '—';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Back link */}
        <Link
          to="/feed"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium"
        >
          <ArrowLeft size={16} />
          {t.backToFeed}
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">
              {t.title}
            </h1>
            <p className="text-sm text-gray-600">{t.subtitle}</p>
            <p className="text-xs text-gray-500 mt-2">
              {t.monthLabel}: <span className="font-medium">{period}</span>
              {fetchedAt && (
                <span className="ml-3 text-gray-400">
                  · {t.asOf} {formatTimestamp(fetchedAt, lang)}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            <RefreshCw
              size={14}
              className={refreshing ? 'animate-spin' : ''}
            />
            {refreshing ? t.refreshing : t.refresh}
          </button>
        </div>

        {/* Cards de métricas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Card grande: Interações */}
          <div className="md:col-span-3 bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Activity size={22} className="text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  {t.cardInteractions}
                </p>
                <p className="text-3xl font-bold text-gray-900 mb-1">
                  {formatNumber(interactions, lang)}
                </p>
                <p className="text-sm text-gray-600">{usageRatioText}</p>
                <p className="text-xs text-gray-500 mt-2">
                  {t.cardInteractionsHelp}
                </p>
              </div>
            </div>
          </div>

          {/* Card: Tokens */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Zap size={18} className="text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  {t.cardTokens}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(tokens, lang)}
                </p>
                <p className="text-xs text-gray-500 mt-2">{t.cardTokensHelp}</p>
              </div>
            </div>
          </div>

          {/* Card: Cota utilizada (%) — substitui o antigo card de custo USD */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 md:col-span-2">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Activity size={18} className="text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  {t.cardCost}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {quotaPct ?? '—'}
                </p>
                {quotaPct && (
                  <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gray-700 transition-all"
                      style={{
                        width: `${Math.min(
                          (interactions / (subscription?.ai_credits_monthly || 1)) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  {quotaPct ? t.cardCostHelp : t.cardCostNoSub}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabela: Por assistente */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              {t.byAgentTitle}
            </h2>
          </div>
          {byAgent.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Bot size={32} className="mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-gray-500">{t.byAgentEmpty}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      {t.colAgent}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      {t.colInteractions}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      {t.colTokens}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byAgent.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <Bot size={14} className="text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {formatAgentName(row.agent_name)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-right font-mono text-gray-900">
                        {formatNumber(row.interactions, lang)}
                      </td>
                      <td className="px-6 py-3 text-right font-mono text-gray-700">
                        {formatNumber(row.tokens, lang)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Contexto do plano (texto honesto) */}
        <div className="bg-gray-100 border border-gray-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-md bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
              <Info size={16} className="text-gray-600" />
            </div>
            <div className="flex-1 min-w-0 text-sm text-gray-700">
              <p className="font-semibold text-gray-900 mb-2">
                {t.contextTitle}
              </p>
              {subscription && club ? (
                <>
                  <p className="mb-1">{t.contextLineClub(club.club_name)}</p>
                  <p className="mb-3">
                    {t.contextLinePlan(
                      subscription.plan_key,
                      subscription.ai_credits_monthly
                    )}
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    {t.contextHonest}
                  </p>
                </>
              ) : (
                <p className="text-gray-600 leading-relaxed">
                  {t.contextNoSub}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyUsagePage;
